import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Res, PrintPreviewDTO, Permission, SalesOrderDTO } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { startOfDay } from 'date-fns';
import { Col, Cols, Flex, TemplatePrintPreview } from 'core/ui';
import { PrintFacade, SalesOrderFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { legacyControlsFor } from 'core/ui-next/controls';
import { Modal } from 'core/ui-next/modal';

type Props = {
	orders: SalesOrderDTO[]
} & Modal.Actions<void>;

type State = {
	gridHeight: number,
	date: Date,
	orders: SalesOrderDTO[],
	preview: Nullable<PrintPreviewDTO>
};


async function getByDeliveryDate(deliveryDate: Date): Promise<SalesOrderDTO[]> {
	const orders = await SalesOrderFacade.getByDeliveryDate(deliveryDate);
	return orders;
}

class ShowSalesOrdersPreparation extends PosScreenClassComponent<Props, State> {

	private grid = React.createRef<UI.DataGrid<SalesOrderDTO>>();
	private form = React.createRef<UI.Form<State>>();
	private ref = React.createRef<HTMLDivElement>();

	protected getScreenId(): string {
		return 'SALES_ORDER_PREPARATION';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_SALES_ORDERS_PREPARATION();
	}

	protected getImage(): string | SvgIcon {
		return Images.ShowSalesOrdersPreparation;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';

	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			{ title: Res.Web.SHOW_SALES_ORDERS_PREPARATION_PRINT_BTN(), image: Images.PrintTicket, action: () => this.printPreview() }
		];
	}

	protected useKeypad() {
		return false;
	}

	createInitialState(props: Props): Nullable<State> {
		return {
			gridHeight: 400,
			date: startOfDay(new Date()),
			preview: null,
			orders: [...props.orders]
		};
	}

	async onComponentDidMount(): Promise<void> {
		UI.ResizeListener.add(this.ref.current!, this.fixTableHeight);
		this.fixTableHeight();

		await this.updateSalesOrders();
	}

	fixTableHeight = () => {
		if (!this.mounted)
			return;

		const container = document.getElementById('pendingSalesOrderGridContainer');
		this.setState({
			gridHeight: container!.clientHeight
		});
	};


	private updateSalesOrders = async () => {
		const orders = this.props.orders.length === 0 ? await getByDeliveryDate(this.state.date) : [...this.props.orders];
		const preview = await PrintFacade.getSalesOrderPreviews(orders);
		this.setState({ orders, preview }, () => this.grid.current?.selectItems(orders));
	};

	onComponentWillUnmount(): void {
		UI.ResizeListener.remove(this.ref.current!);
	}

	printPreview = () => PrintFacade.printPreparationSummary(this.grid.current!.getSelectedItems().map(order => order.TicketId));

	onSelectedSalesOrderChanged = async (): Promise<void> => {
		const preview = await PrintFacade.getSalesOrderPreviews(this.grid.current!.getSelectedItems());
		this.setState({ preview });
	};

	onDateChange = async () => {
		const valid = await this.form.current!.updateModel();
		if (valid) {
			await this.updateSalesOrders();
		}
	};

	protected getContent(): React.ReactNode {

		const { Form } = UI.controlsFor<State>();
		const { DatePicker } = legacyControlsFor<State>();

		const { DataGrid } = legacyControlsFor<SalesOrderDTO>();

		const columns: UI.DataGridColumn<SalesOrderDTO>[] = [
			{ title: Res.Web.SHOW_SALES_ORDERS_NUMBER_CLM(), linkTo: x => x.SerialNumber, width: 80 },
			{ title: Res.Web.SHOW_SALES_ORDERS_CUSTOMER_NAME_CLM(), linkTo: x => x.Customer.FiscalName, width: 200, flexGrow: 1 },
			{ title: Res.Web.SHOW_SALES_ORDERS_DELIVERY_DATE_CLM(), linkTo: x => x.DeliveryDate, format: UI.Format.asDateAndTime, width: 100, },
			{ title: Res.Web.SHOW_SALES_ORDERS_NET_AMOUNT_CLM(), linkTo: x => x.NetAmount, format: UI.Format.asMoney, width: 90, cellClassName: 'text-center', headerClassName: 'text-center' },
			{ title: Res.Web.SHOW_SALES_ORDERS_PENDING_AMOUNT_CLM(), linkTo: x => x.PendingAmount, format: UI.Format.asMoney, width: 90, cellClassName: 'text-center', headerClassName: 'text-center' },
			{ title: Res.Web.SHOW_SALES_ORDERS_PREPAID_AMOUNT_CLM(), linkTo: x => x.PrepaidAmount, format: UI.Format.asMoney, width: 90, cellClassName: 'text-center', headerClassName: 'text-center' },
			{ title: Res.Web.SHOW_SALES_ORDERS_LOCATION_CLM(), linkTo: x => x.Location, width: 150 },
			{ title: Res.Web.SHOW_SALES_ORDERS_USER_CLM(), linkTo: x => x.UserName, width: 120 },
			{ title: Res.Web.SHOW_SALES_ORDERS_NOTES_CLM(), linkTo: x => x.Notes, width: 300, format: notes => notes.replace(/\n/g, ' ') }
		];

		return <Flex.Horizontal ref={this.ref}>
			<Flex.Item size='*' className='border-right'>
				<Flex.Vertical>
					{this.props.orders.length === 0 ?
						<Flex.Item>
							<Form className='m-t-sm' model={this.state} ref={this.form}>
								<Cols style={{ justifyContent: 'center' }}>
									<Col className='m-t-xs m-r-xs text-16'>
										{Res.Web.SHOW_SALES_ORDERS_PREPARATION_DELIVERY_DATE_LBL()}
									</Col>
									<Col>
										<DatePicker className='text-center' linkTo={x => x.date} onValueChanged={() => this.onDateChange()} required tabIndex={-1} />
									</Col>
								</Cols>
							</Form>
						</Flex.Item> : null
					}
					<Flex.Item id='pendingSalesOrderGridContainer' size='*'
						style={{ overflow: 'hidden' /* Necesario para calcular bien la altura a la primera */ }}
					>
						<DataGrid
							ref={this.grid}
							id='pendingSalesOrderGrid'
							initialData={this.state.orders}
							filtrable
							hideExportButton={true}
							multiSelect={true}
							height={this.state.gridHeight}
							itemKey={x => x.Id}
							columns={columns}
							onSelectedItemsChanged={() => this.onSelectedSalesOrderChanged()}
							shouldPersistState={false}
						/>
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
			<Flex.Item size='425'>
				<Flex.Vertical>
					<Flex.Item size='*'>
						<div className='m-sm'>
							{this.state.preview && this.state.preview.EscPosPreview ? <TemplatePrintPreview preview={this.state.preview} /> : null}
						</div>
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal>;
	}
}

declare global {
	interface CommandType {
		showSaleOrdersPreparations: (orders: SalesOrderDTO[]) => Promise<Result>
	}
}

Command.register('showSaleOrdersPreparations', Preconditions.and(Preconditions.ensure(Permission.SalesOrder_ShowPreparation), Preconditions.ensureIsNotLite), async (orders: SalesOrderDTO[]) => {
	await Modal.show(ShowSalesOrdersPreparation, { orders });
	return Result.ok();
});